import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
// web/src/rendering/components/leaves/StatCards.tsx
// Renders an array of stat cards in a responsive grid.
// Backend emits: {component: "stat_cards", props: {cards: [{label, value, sub?, trend?, badge?}]}}
// trend: "up" | "down" | "neutral" → maps to StatCard tone.
// badge (CR-2026-05-07-001 PR-H): an account_type string (retirement_account,
// brokerage, etc.) — renders a coloured pill in the card header.
import { memo } from "react";
import { StatCard } from "./StatCard";
const TREND_TONE = {
    up: "positive",
    down: "negative",
    neutral: "default",
};
// Account type → human label + color (matches ProfilePage badge palette).
const BADGE_LABEL = {
    retirement_account: "Retirement",
    brokerage: "Brokerage",
    savings: "Savings",
    crypto: "Crypto",
    property: "Property",
    business: "Business",
    life_insurance: "Insurance",
    vehicle: "Vehicle",
    other: "Other",
};
const BADGE_CLASS = {
    retirement_account: "bg-amber-500/20 text-amber-400",
    brokerage: "bg-blue-500/20 text-blue-400",
    savings: "bg-emerald-500/20 text-emerald-400",
    crypto: "bg-purple-500/20 text-purple-400",
    property: "bg-slate-500/20 text-slate-400",
    business: "bg-gray-500/20 text-gray-400",
    life_insurance: "bg-cyan-500/20 text-cyan-400",
    vehicle: "bg-gray-500/20 text-gray-400",
    other: "bg-gray-500/20 text-gray-400",
};
function StatCardsImpl({ cards = [] }) {
    if (cards.length === 0)
        return null;
    // CR-2026-05-24-001 Batch 8: responsive grid — 2 cols on mobile, 3-4 on
    // larger screens. Hard-coded grid-cols-N classes break Tailwind's JIT for
    // values not in the safelist; explicit cases avoid that pitfall.
    const cols = cards.length;
    const gridCls = cols >= 4 ? "grid-cols-2 sm:grid-cols-2 md:grid-cols-4" :
        cols === 3 ? "grid-cols-2 sm:grid-cols-3" :
            cols === 2 ? "grid-cols-2" :
                "grid-cols-1";
    return (_jsx("div", { className: `grid gap-3 ${gridCls}`, children: cards.map((c, i) => {
            const badgeKey = c.badge ?? "";
            const badgeLabel = BADGE_LABEL[badgeKey];
            const badgeCls = BADGE_CLASS[badgeKey] ?? "bg-slate-700 text-slate-300";
            return (_jsxs("div", { className: "relative", children: [_jsx(StatCard, { label: c.label, value: c.value, sublabel: c.sub, tone: c.trend ? (TREND_TONE[c.trend] ?? "default") : "default" }), badgeLabel && (_jsx("span", { className: `absolute top-2 right-2 text-[10px] px-1.5 py-0.5 rounded ${badgeCls}`, children: badgeLabel }))] }, i));
        }) }));
}
// Memoised — see PieChart for rationale.
export const StatCards = memo(StatCardsImpl);
